import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Input,
  Select,
  Tooltip,
  Typography,
  Empty,
  Badge,
  Divider,
  Spin,
  Progress,
  List,
  message,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  TrophyOutlined,
  FireOutlined,
  RiseOutlined,
  SearchOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  StarOutlined,
  GiftOutlined,
  CrownOutlined,
  TeamOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { Level } from './types';
import { useTheme } from '../../../contexts/ThemeContext';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const { Text } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatCard = styled(Card)`
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

export const PlayersTab: React.FC = () => {
  const [players, setPlayers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [searchText, setSearchText] = useState('');
  const [globalStats, setGlobalStats] = useState<any | null>(null);

  useEffect(() => {
    loadData();
  }, [selectedLevel]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar datos en paralelo
      const [levelsData, playersData, globalStatsData] = await Promise.all([
        gamificationApi.getLevels(),
        gamificationApi.getPlayers(selectedLevel),
        gamificationApi.getGlobalStats(),
      ]);

      setLevels(levelsData);
      setPlayers(playersData);
      setGlobalStats(globalStatsData);
    } catch (error) {
      message.error('Error al cargar los datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Usuario',
      dataIndex: ['panelUser', 'username'],
      key: 'username',
      sorter: (a, b) => a.panelUser?.username.localeCompare(b.panelUser?.username),
      render: (username) => <Text strong>{username}</Text>,
    },
    {
      title: 'Nivel',
      dataIndex: 'currentLevel',
      key: 'currentLevel',
      sorter: (a, b) => a.currentLevel - b.currentLevel,
      render: (level) => {
        const levelData = levels.find(l => l.levelNumber === level);
        return (
          <Space>
            <span style={{ fontSize: '20px' }}>{levelData?.icon || '🎮'}</span>
            <div>
              <div><Text strong>Nivel {level}</Text></div>
              <div><Text type="secondary">{levelData?.name || 'Unknown'}</Text></div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Racha Actual',
      dataIndex: 'currentStreak',
      key: 'currentStreak',
      sorter: (a, b) => a.currentStreak - b.currentStreak,
      render: (streak) => (
        <Space>
          <FireOutlined style={{ color: streak > 0 ? '#ff4d4f' : '#ccc' }} />
          <Text>{streak} días</Text>
        </Space>
      ),
    },
    {
      title: 'Bonus Acumulado',
      dataIndex: 'lifetimeBonus',
      key: 'lifetimeBonus',
      sorter: (a, b) => a.lifetimeBonus - b.lifetimeBonus,
      render: (bonus) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${bonus?.toFixed(2) || '0.00'}
        </Text>
      ),
    },
    {
      title: 'Puntos',
      key: 'points',
      sorter: (a, b) => (a.pointsStats?.currentPoints || 0) - (b.pointsStats?.currentPoints || 0),
      render: (_, record) => {
        const currentPoints = record.pointsStats?.currentPoints || 0;
        const totalEarned = record.pointsStats?.totalPointsEarned || 0;
        return (
          <Tooltip title={`Disponibles: ${currentPoints} pts | Total acumulado: ${totalEarned} pts`}>
            <Space direction="vertical" size={0}>
              <Space size={4}>
                <StarOutlined style={{ color: '#faad14' }} />
                <Text strong style={{ color: '#1890ff' }}>{currentPoints} pts</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Total: {totalEarned} pts
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Cargas',
      dataIndex: 'totalLoads',
      key: 'totalLoads',
      sorter: (a, b) => a.totalLoads - b.totalLoads,
      render: (loads) => <Badge count={loads} showZero style={{ backgroundColor: '#1890ff' }} />,
    },
    {
      title: 'Total Cargado',
      dataIndex: 'totalAmountLoaded',
      key: 'totalAmountLoaded',
      sorter: (a, b) => a.totalAmountLoaded - b.totalAmountLoaded,
      render: (amount) => `$${amount?.toFixed(2) || '0.00'}`,
    },
    {
      title: 'Última Carga',
      dataIndex: 'lastLoadDate',
      key: 'lastLoadDate',
      sorter: (a, b) => new Date(a.lastLoadDate).getTime() - new Date(b.lastLoadDate).getTime(),
      render: (date) => date ? new Date(date).toLocaleDateString('es-AR') : '-',
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Estadísticas Globales Completas */}
      {globalStats && (
        <>
          {/* Tarjetas de Resumen Principal */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>👥 Total Usuarios</span>}
                  value={globalStats.overview.totalUsers}
                  valueStyle={{ color: '#fff', fontSize: '32px' }}
                  suffix={<span style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>usuarios</span>}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  border: 'none'
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>💰 Bonus Total Otorgado</span>}
                  value={globalStats.overview.totalBonusGiven}
                  precision={2}
                  prefix={<span style={{ color: '#fff' }}>$</span>}
                  valueStyle={{ color: '#fff', fontSize: '32px' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  border: 'none'
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>👥 Total Referidos</span>}
                  value={globalStats.overview.totalReferrals}
                  valueStyle={{ color: '#fff', fontSize: '32px' }}
                  suffix={<span style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>referidos</span>}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                style={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  border: 'none'
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>🔥 Racha Promedio</span>}
                  value={globalStats.overview.averageStreak}
                  precision={1}
                  valueStyle={{ color: '#fff', fontSize: '32px' }}
                  suffix={<span style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>días</span>}
                />
              </StatCard>
            </Col>
          </Row>

          {/* Tarjetas de Estadísticas de Puntos */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <StatCard>
                <Statistic
                  title="⭐ Total Puntos Acumulados"
                  value={globalStats.points.totalEarned}
                  precision={0}
                  valueStyle={{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
                  suffix="pts"
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={8}>
              <StatCard>
                <Statistic
                  title="⭐ Puntos Canjeados"
                  value={globalStats.points.totalRedeemed}
                  precision={0}
                  valueStyle={{ color: '#ff4d4f', fontSize: '28px', fontWeight: 'bold' }}
                  suffix="pts"
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={8}>
              <StatCard>
                <Statistic
                  title="⭐ Puntos Disponibles"
                  value={globalStats.points.currentTotal}
                  precision={0}
                  valueStyle={{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }}
                  suffix="pts"
                />
              </StatCard>
            </Col>
          </Row>

          {/* Tablas de Rankings y Distribución */}
          <Row gutter={[16, 16]}>
            {/* Distribución por Niveles */}
            <Col xs={24} lg={12}>
              <StyledCard
                title={
                  <Space>
                    <CrownOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                    <Text strong style={{ fontSize: '16px' }}>Distribución por Niveles</Text>
                  </Space>
                }
                style={{ height: '100%' }}
              >
                {globalStats.levelDistribution && globalStats.levelDistribution.length > 0 ? (
                  <div style={{ height: '300px' }}>
                    <Bar
                      data={{
                        labels: globalStats.levelDistribution.map((level: any) => {
                          const levelData = levels.find(l => l.levelNumber === level.currentLevel);
                          return `${levelData?.icon || '🎮'} Nivel ${level.currentLevel}`;
                        }),
                        datasets: [
                          {
                            label: 'Usuarios',
                            data: globalStats.levelDistribution.map((level: any) => level.count),
                            backgroundColor: [
                              'rgba(102, 126, 234, 0.7)',
                              'rgba(118, 75, 162, 0.7)',
                              'rgba(240, 147, 251, 0.7)',
                              'rgba(245, 87, 108, 0.7)',
                              'rgba(79, 172, 254, 0.7)',
                              'rgba(0, 242, 254, 0.7)',
                              'rgba(250, 112, 154, 0.7)',
                              'rgba(254, 225, 64, 0.7)',
                            ],
                            borderColor: [
                              'rgba(102, 126, 234, 1)',
                              'rgba(118, 75, 162, 1)',
                              'rgba(240, 147, 251, 1)',
                              'rgba(245, 87, 108, 1)',
                              'rgba(79, 172, 254, 1)',
                              'rgba(0, 242, 254, 1)',
                              'rgba(250, 112, 154, 1)',
                              'rgba(254, 225, 64, 1)',
                            ],
                            borderWidth: 2,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: 12,
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            callbacks: {
                              label: (context) => {
                                const levelIndex = context.dataIndex;
                                const levelData = globalStats.levelDistribution[levelIndex];
                                return `${levelData.levelName}: ${context.parsed.y} usuario${context.parsed.y !== 1 ? 's' : ''}`;
                              },
                            },
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1,
                              font: {
                                size: 12,
                              },
                            },
                            title: {
                              display: true,
                              text: 'Cantidad de Usuarios',
                              font: {
                                size: 13,
                                weight: 'bold',
                              },
                            },
                            grid: {
                              color: 'rgba(0, 0, 0, 0.05)',
                            },
                          },
                          x: {
                            ticks: {
                              font: {
                                size: 11,
                              },
                            },
                            grid: {
                              display: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <Empty description="Sin datos de distribución de niveles" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </StyledCard>
            </Col>

            {/* Top 10 Rachas Activas */}
            <Col xs={24} lg={12}>
              <StyledCard
                title={
                  <Space>
                    <FireOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
                    <Text strong style={{ fontSize: '16px' }}>Top 10 Rachas Activas</Text>
                  </Space>
                }
                style={{ height: '100%' }}
              >
                {globalStats.topStreaks && globalStats.topStreaks.length > 0 ? (
                  <Table
                    dataSource={globalStats.topStreaks.slice(0, 10)}
                    pagination={false}
                    size="small"
                    scroll={{ y: 240 }}
                    columns={[
                      {
                        title: '#',
                        key: 'rank',
                        width: 40,
                        render: (_: any, __: any, index: number) => {
                          const medals = ['🥇', '🥈', '🥉'];
                          return index < 3 ? (
                            <span style={{ fontSize: '20px' }}>{medals[index]}</span>
                          ) : (
                            <Text type="secondary">{index + 1}</Text>
                          );
                        },
                      },
                      {
                        title: 'Usuario',
                        dataIndex: 'username',
                        key: 'username',
                        render: (username: string) => <Text strong>{username}</Text>,
                      },
                      {
                        title: 'Racha',
                        dataIndex: 'currentStreak',
                        key: 'currentStreak',
                        align: 'right',
                        render: (streak: number) => (
                          <Tag color="red" style={{ fontSize: '13px', fontWeight: 'bold' }}>
                            🔥 {streak} días
                          </Tag>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Empty description="No hay rachas activas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </StyledCard>
            </Col>

            {/* Top 10 Usuarios Más Activos */}
            <Col xs={24} lg={12}>
              <StyledCard
                title={
                  <Space>
                    <RiseOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    <Text strong style={{ fontSize: '16px' }}>Top 10 Usuarios Más Activos</Text>
                  </Space>
                }
                style={{ height: '100%' }}
              >
                {globalStats.mostActiveUsers && globalStats.mostActiveUsers.length > 0 ? (
                  <Table
                    dataSource={globalStats.mostActiveUsers.slice(0, 10)}
                    pagination={false}
                    size="small"
                    scroll={{ y: 240 }}
                    columns={[
                      {
                        title: '#',
                        key: 'rank',
                        width: 40,
                        render: (_: any, __: any, index: number) => {
                          const medals = ['🥇', '🥈', '🥉'];
                          return index < 3 ? (
                            <span style={{ fontSize: '20px' }}>{medals[index]}</span>
                          ) : (
                            <Text type="secondary">{index + 1}</Text>
                          );
                        },
                      },
                      {
                        title: 'Usuario',
                        dataIndex: 'username',
                        key: 'username',
                        render: (username: string) => <Text strong>{username}</Text>,
                      },
                      {
                        title: 'Cargas',
                        dataIndex: 'totalLoads',
                        key: 'totalLoads',
                        align: 'center',
                        render: (loads: number) => (
                          <Badge count={loads} showZero style={{ backgroundColor: '#1890ff' }} />
                        ),
                      },
                      {
                        title: 'Total',
                        dataIndex: 'totalAmount',
                        key: 'totalAmount',
                        align: 'right',
                        render: (amount: number) => (
                          <Text strong style={{ color: '#52c41a' }}>
                            ${amount.toFixed(2)}
                          </Text>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Empty description="Sin datos de actividad" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </StyledCard>
            </Col>
          </Row>

          <Divider />
        </>
      )}

      <StyledCard
        title={
          <Space>
            <UserOutlined style={{ color: '#ac4949' }} />
            <span>Jugadores del Sistema de Gamificación</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Input
              placeholder="Buscar por nombre de usuario"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              style={{ width: 200 }}
              placeholder="Todos los niveles"
              allowClear
              value={selectedLevel}
              onChange={setSelectedLevel}
            >
              {levels.map(level => (
                <Option key={level.levelNumber} value={level.levelNumber}>
                  <Space>
                    <span>{level.icon}</span>
                    <span>Nivel {level.levelNumber} - {level.name}</span>
                  </Space>
                </Option>
              ))}
            </Select>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={players.filter(player =>
            !searchText ||
            player.panelUser?.username?.toLowerCase().includes(searchText.toLowerCase())
          )}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender: (record) => <ExpandedPlayerRow record={record} />,
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
            expandIcon: ({ expanded, onExpand, record }) => (
              <Tooltip title={expanded ? "Ocultar detalles" : "Ver detalles"}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={(e) => onExpand(record, e)}
                  style={{ color: expanded ? '#ac4949' : '#666' }}
                />
              </Tooltip>
            ),
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} jugador${total !== 1 ? 'es' : ''}`,
          }}
        />
      </StyledCard>
    </Space>
  );
};

// Componente separado para la fila expandible
const ExpandedPlayerRow: React.FC<{ record: any }> = ({ record }) => {
  const { theme } = useTheme();
  const [referralStats, setReferralStats] = useState<any>(null);
  const [streakStats, setStreakStats] = useState<any>(null);
  const [levelProgression, setLevelProgression] = useState<any>(null);
  const [rolloverData, setRolloverData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
      loadUserStats();
    }, []);

    const loadUserStats = async () => {
      try {
        setStatsLoading(true);
        const [referrals, streaks, progression, rollover] = await Promise.all([
          gamificationApi.getUserReferralStats(record.userId),
          gamificationApi.getUserStreakStats(record.userId),
          gamificationApi.getUserLevelProgression(record.userId),
          gamificationApi.getUserActiveRollover(record.userId).catch(() => null),
        ]);
        setReferralStats(referrals);
        setStreakStats(streaks);
        setLevelProgression(progression);
        setRolloverData(rollover);
      } catch (error) {
        console.error('Error loading user stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    const bonusColumns: ColumnsType<any> = [
      {
        title: 'Fecha y Hora',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 130,
        render: (date) => (
          <div>
            <div style={{ fontWeight: 500 }}>{new Date(date).toLocaleDateString('es-AR')}</div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {new Date(date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </div>
        ),
      },
      {
        title: 'Evento',
        key: 'event',
        width: 100,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return (
              <Tag color="cyan" icon={<CalendarOutlined />} style={{ fontWeight: 'bold' }}>
                Primer Ingreso
              </Tag>
            );
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            const isEarned = historyRecord.transactionType === 'EARNED';
            return (
              <Tag color={isEarned ? 'green' : 'red'} icon={<StarOutlined />}>
                {isEarned ? 'Puntos +' : 'Canje'}
              </Tag>
            );
          }
          return <Tag color="blue" icon={<GiftOutlined />}>Bonus</Tag>;
        },
      },
      {
        title: 'Descripción',
        key: 'description',
        width: 80,
        ellipsis: {
          showTitle: false,
        },
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return (
              <Text strong style={{ color: '#13c2c2' }}>
                Primer login exitoso en la plataforma
              </Text>
            );
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            const text = historyRecord.description || historyRecord.sourceSystem || 'Transacción';
            return (
              <Tooltip title={text}>
                <div style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {text}
                </div>
              </Tooltip>
            );
          }
          const text = historyRecord.ruleName || 'Bonus';
          return (
            <Tooltip title={text}>
              <div style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: 'Tipo',
        key: 'bonusType',
        align: 'center',
        width: 80,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            return <Tag color="purple">Puntos</Tag>;
          }
          const typeMap: Record<string, { color: string; label: string }> = {
            PERCENTAGE: { color: 'blue', label: '%' },
            FIXED_AMOUNT: { color: 'green', label: 'Fijo' },
            POINTS: { color: 'purple', label: 'Pts' }
          };
          const config = typeMap[historyRecord.bonusType] || { color: 'default', label: historyRecord.bonusType };
          return <Tag color={config.color}>{config.label}</Tag>;
        }
      },
      {
        title: 'Monto',
        key: 'amount',
        align: 'right',
        width: 120,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            const isPositive = historyRecord.amount > 0;
            return (
              <div>
                <Text strong style={{ color: isPositive ? '#52c41a' : '#ff4d4f', fontSize: '14px' }}>
                  {isPositive ? '+' : ''}{historyRecord.amount} pts
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Saldo: {historyRecord.balanceAfter} pts
                  </Text>
                </div>
              </div>
            );
          }
          return (
            <div>
              {historyRecord.loadAmount && (
                <div>
                  <Text type="secondary" style={{ fontSize: '11px' }}>Carga: ${historyRecord.loadAmount?.toFixed(0)}</Text>
                </div>
              )}
              <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
                +${historyRecord.bonusAmount?.toFixed(2) || '0.00'}
              </Text>
            </div>
          );
        },
      },
      {
        title: 'Mult.',
        key: 'appliedMultiplier',
        align: 'center',
        width: 70,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            return <Text type="secondary">-</Text>;
          }
          const mult = historyRecord.data?.appliedMultiplier || 1;
          return (
            <Tag color={mult > 1 ? 'green' : 'default'}>
              x{mult.toFixed(1)}
            </Tag>
          );
        },
      },
      {
        title: 'Nivel',
        dataIndex: 'userLevel',
        key: 'userLevel',
        align: 'center',
        width: 70,
        render: (level, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            return <Text type="secondary">-</Text>;
          }
          return <Text>🏆 {level || 1}</Text>;
        },
      },
      {
        title: 'Racha',
        key: 'userStreak',
        align: 'center',
        width: 70,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            return <Text type="secondary">-</Text>;
          }
          const streak = historyRecord.data?.userStreak || 0;
          return <Text>{streak > 0 ? `🔥 ${streak}d` : '-'}</Text>;
        },
      },
      {
        title: 'Carga #',
        key: 'userLoadCount',
        align: 'center',
        width: 100,
        render: (_, historyRecord) => {
          // Fila especial de Primer Login
          if (historyRecord.type === 'FIRST_LOGIN') {
            return <Text type="secondary">-</Text>;
          }

          if (historyRecord.type === 'POINTS_TRANSACTION') {
            return <Text type="secondary">-</Text>;
          }
          const count = historyRecord.data?.userLoadCount || 0;
          const wasFirstLoadEver = historyRecord.data?.wasFirstLoadEver;
          const wasFirstLoadToday = historyRecord.data?.wasFirstLoadToday;

          return (
            <Space size={4}>
              <Text type="secondary">#{count}</Text>
              {wasFirstLoadEver && (
                <Tooltip title="Primera carga histórica">
                  <span style={{ fontSize: '16px' }}>⭐</span>
                </Tooltip>
              )}
              {wasFirstLoadToday && (
                <Tooltip title="Primera carga del día">
                  <Tag color="cyan" style={{ margin: 0, fontSize: '10px' }}>1°</Tag>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ];

    return (
      <Card
        size="small"
        style={{
          margin: '8px 0',
          backgroundColor: '#fafafa',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header con estadísticas resumidas */}
        <Card
          size="small"
          style={{
            marginBottom: 20,
            backgroundColor: 'white',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="💰 Bonus Total Acumulado"
                value={record.lifetimeBonus}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="📊 Total Cargado"
                value={record.totalAmountLoaded}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="📈 Cargas Realizadas"
                value={record.totalLoads}
                suffix="cargas"
                valueStyle={{ fontSize: '20px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="🔥 Racha Máxima"
                value={record.longestStreak}
                suffix="días"
                valueStyle={{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Tarjeta de Primer Ingreso a la Plataforma */}
        {record.firstLoginDate && (
          <Card
            size="small"
            style={{
              marginBottom: 20,
              backgroundColor: theme === 'dark' ? 'rgba(19, 194, 194, 0.1)' : '#e6fffb',
              borderRadius: '8px',
              border: theme === 'dark' ? '1px solid rgba(19, 194, 194, 0.3)' : '1px solid #87e8de'
            }}
          >
            <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
              <Space>
                <CalendarOutlined style={{ color: '#13c2c2' }} />
                <Text strong style={{ fontSize: '16px' }}>Primer Ingreso a la Plataforma</Text>
              </Space>
            </Divider>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Statistic
                  title="📅 Fecha de Primer Login Exitoso"
                  value={new Date(record.firstLoginDate).toLocaleDateString('es-AR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  valueStyle={{ color: '#13c2c2', fontSize: '18px', fontWeight: 'bold' }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Statistic
                  title="🕐 Hora de Primer Login"
                  value={new Date(record.firstLoginDate).toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                  valueStyle={{ color: '#13c2c2', fontSize: '18px', fontWeight: 'bold' }}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Tiempo desde el primer ingreso: {' '}
                {(() => {
                  const now = new Date();
                  const firstLogin = new Date(record.firstLoginDate);
                  const diffMs = now.getTime() - firstLogin.getTime();
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                  if (diffDays > 0) {
                    return `${diffDays} día${diffDays !== 1 ? 's' : ''} y ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
                  } else if (diffHours > 0) {
                    return `${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
                  } else {
                    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                    return `${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
                  }
                })()}
              </Text>
            </div>
          </Card>
        )}

        {/* Estadísticas de Puntos */}
        {record.pointsStats && (
          <Card
            size="small"
            style={{
              marginBottom: 20,
              backgroundColor: '#fff7e6',
              borderRadius: '8px',
              border: '1px solid #ffd591'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="⭐ Total Puntos Acumulados"
                  value={record.pointsStats.totalPointsEarned}
                  precision={0}
                  suffix="pts"
                  valueStyle={{ color: '#3f8600', fontSize: '20px', fontWeight: 'bold' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="⭐ Puntos Disponibles"
                  value={record.pointsStats.currentPoints}
                  precision={0}
                  suffix="pts"
                  valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="⭐ Puntos Canjeados"
                  value={record.pointsStats.totalPointsRedeemed}
                  precision={0}
                  suffix="pts"
                  valueStyle={{ color: '#cf1322', fontSize: '20px', fontWeight: 'bold' }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Estadísticas de Referidos */}
        {statsLoading ? (
          <Card size="small" style={{ marginBottom: 20, textAlign: 'center' }}>
            <Spin tip="Cargando estadísticas adicionales..." />
          </Card>
        ) : (
          <>
            {referralStats && (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  backgroundColor: '#e6f7ff',
                  borderRadius: '8px',
                  border: '1px solid #91d5ff'
                }}
              >
                <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
                  <Space>
                    <TeamOutlined style={{ color: '#1890ff' }} />
                    <Text strong style={{ fontSize: '16px' }}>Sistema de Referidos</Text>
                  </Space>
                </Divider>

                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="👥 Total Referidos"
                      value={referralStats.totalReferrals}
                      suffix="usuarios"
                      valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="📊 Cargas de Referidos"
                      value={referralStats.totalReferredLoads}
                      suffix="cargas"
                      valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="💰 Monto Total Generado"
                      value={referralStats.totalReferredAmount}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#faad14', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    {referralStats.wasReferred ? (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Referido por:</Text>
                        <div style={{ marginTop: 4 }}>
                          <Tag color="blue" icon={<UserOutlined />} style={{ fontSize: '14px' }}>
                            {referralStats.referredBy}
                          </Tag>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: 8 }}>
                        <Text type="secondary">Sin referidor</Text>
                      </div>
                    )}
                  </Col>
                </Row>

                {referralStats.referralsGiven && referralStats.referralsGiven.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Divider orientation="left" style={{ fontSize: '14px', marginTop: 16, marginBottom: 12 }}>
                      <Text strong>Lista de Referidos ({referralStats.totalReferrals})</Text>
                    </Divider>
                    <List
                      dataSource={referralStats.referralsGiven}
                      renderItem={(item: any) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                            title={<Text strong>{item.username}</Text>}
                            description={
                              <Space size="small">
                                <Tag color="green">{item.loadCount} cargas</Tag>
                                <Tag color="orange">${item.totalAmount.toFixed(2)}</Tag>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Desde: {new Date(item.joinedAt).toLocaleDateString('es-AR')}
                                </Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                      size="small"
                      bordered
                      style={{ maxHeight: '200px', overflow: 'auto' }}
                    />
                  </div>
                )}
              </Card>
            )}

            {/* Rollover Activo */}
            {rolloverData ? (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  backgroundColor: '#fff2e8',
                  borderRadius: '8px',
                  border: '1px solid #ffbb96'
                }}
              >
                <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
                  <Space>
                    <ThunderboltOutlined style={{ color: '#fa541c' }} />
                    <Text strong style={{ fontSize: '16px' }}>Rollover Activo</Text>
                    <Tag color="orange">{rolloverData.status}</Tag>
                  </Space>
                </Divider>

                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Depósito"
                      value={rolloverData.depositAmount}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#fa541c', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Bonus Total"
                      value={rolloverData.totalBonusAmount}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Rollover Requerido"
                      value={rolloverData.rolloverRequired}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
                      suffix={<Text type="secondary" style={{ fontSize: '12px' }}>x{rolloverData.rolloverMultiplier}</Text>}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Anti-Lavado Requerido"
                      value={rolloverData.antiLavadoRequired}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#722ed1', fontSize: '20px', fontWeight: 'bold' }}
                      suffix={<Text type="secondary" style={{ fontSize: '12px' }}>x{rolloverData.antiLavadoMultiplier}</Text>}
                    />
                  </Col>
                </Row>

                <div style={{ marginTop: 16 }}>
                  <Space wrap>
                    <Text type="secondary">Reglas aplicadas:</Text>
                    {rolloverData.appliedRules?.map((rule: string, idx: number) => (
                      <Tag key={idx} color="blue">{rule}</Tag>
                    ))}
                  </Space>
                </div>

                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Activado: {new Date(rolloverData.activatedAt).toLocaleString('es-AR')}
                  </Text>
                  <Popconfirm
                    title="¿Cancelar rollover?"
                    description="Esta acción no se puede deshacer. El rollover será cancelado."
                    onConfirm={async () => {
                      try {
                        await gamificationApi.cancelRollover(rolloverData.id);
                        message.success('Rollover cancelado exitosamente');
                        setRolloverData(null);
                      } catch (error) {
                        message.error('Error al cancelar el rollover');
                      }
                    }}
                    okText="Sí, cancelar"
                    cancelText="No"
                  >
                    <Button danger size="small">Cancelar Rollover</Button>
                  </Popconfirm>
                </div>
              </Card>
            ) : (
              !statsLoading && (
                <Card
                  size="small"
                  style={{
                    marginBottom: 20,
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9'
                  }}
                >
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <Text type="secondary">Sin rollover activo</Text>
                  </div>
                </Card>
              )
            )}

            {/* Estadísticas y Gráfico de Rachas */}
            {streakStats && (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  backgroundColor: '#fff1f0',
                  borderRadius: '8px',
                  border: '1px solid #ffccc7'
                }}
              >
                <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
                  <Space>
                    <FireOutlined style={{ color: '#ff4d4f' }} />
                    <Text strong style={{ fontSize: '16px' }}>Análisis de Rachas</Text>
                  </Space>
                </Divider>

                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="🔥 Racha Actual"
                      value={streakStats.currentStreak}
                      suffix="días"
                      valueStyle={{ color: '#ff4d4f', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="🏆 Racha Máxima"
                      value={streakStats.longestStreak}
                      suffix="días"
                      valueStyle={{ color: '#fa8c16', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="📈 Promedio de Racha"
                      value={streakStats.averageStreak}
                      suffix="días"
                      precision={1}
                      valueStyle={{ color: '#1890ff', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>Última Carga:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Tag color="cyan" icon={<CalendarOutlined />} style={{ fontSize: '13px' }}>
                          {streakStats.lastLoadDate
                            ? new Date(streakStats.lastLoadDate).toLocaleDateString('es-AR')
                            : 'Sin cargas'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                </Row>

                {streakStats.streakHistory && streakStats.streakHistory.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <Text strong style={{ fontSize: '14px', marginBottom: 12, display: 'block' }}>
                      📊 Historial de Rachas (Últimos 30 registros)
                    </Text>
                    <div style={{ height: '200px' }}>
                      <Line
                        data={{
                          labels: streakStats.streakHistory.map((s: any) =>
                            new Date(s.date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
                          ),
                          datasets: [
                            {
                              label: 'Días de Racha',
                              data: streakStats.streakHistory.map((s: any) => s.streakValue),
                              borderColor: '#ff4d4f',
                              backgroundColor: 'rgba(255, 77, 79, 0.1)',
                              fill: true,
                              tension: 0.4,
                              pointRadius: 4,
                              pointHoverRadius: 6,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false,
                            },
                            tooltip: {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              padding: 12,
                              titleColor: '#fff',
                              bodyColor: '#fff',
                            },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                stepSize: 1,
                              },
                              grid: {
                                color: 'rgba(0, 0, 0, 0.05)',
                              },
                            },
                            x: {
                              grid: {
                                display: false,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Estadísticas y Gráfico de Progresión de Niveles */}
            {levelProgression && (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  backgroundColor: '#f6ffed',
                  borderRadius: '8px',
                  border: '1px solid #b7eb8f'
                }}
              >
                <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
                  <Space>
                    <TrophyOutlined style={{ color: '#52c41a' }} />
                    <Text strong style={{ fontSize: '16px' }}>Progresión de Niveles</Text>
                  </Space>
                </Divider>

                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Statistic
                      title="🏆 Nivel Actual"
                      value={levelProgression.currentLevel}
                      suffix={`- ${levelProgression.currentLevelName}`}
                      valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
                        Progreso al Siguiente Nivel
                      </Text>
                      <Progress
                        percent={levelProgression.levelProgress}
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        format={(percent) => `${percent?.toFixed(1)}%`}
                      />
                      {levelProgression.nextLevel && (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="green">
                            Siguiente: Nivel {levelProgression.nextLevel.levelNumber} - {levelProgression.nextLevel.name}
                          </Tag>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Statistic
                      title="💰 Bonus Acumulado Total"
                      value={levelProgression.lifetimeBonus}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: '#fa8c16', fontSize: '20px', fontWeight: 'bold' }}
                    />
                  </Col>
                </Row>

                {levelProgression.progressionHistory && levelProgression.progressionHistory.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <Text strong style={{ fontSize: '14px', marginBottom: 12, display: 'block' }}>
                      📈 Evolución de Nivel en el Tiempo (Últimos 50 puntos)
                    </Text>
                    <div style={{ height: '200px' }}>
                      <Line
                        data={{
                          labels: levelProgression.progressionHistory.map((p: any) =>
                            new Date(p.date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
                          ),
                          datasets: [
                            {
                              label: 'Nivel',
                              data: levelProgression.progressionHistory.map((p: any) => p.level),
                              borderColor: '#52c41a',
                              backgroundColor: 'rgba(82, 196, 26, 0.1)',
                              fill: true,
                              tension: 0.4,
                              pointRadius: 3,
                              pointHoverRadius: 5,
                              yAxisID: 'y',
                            },
                            {
                              label: 'Bonus Acumulado ($)',
                              data: levelProgression.progressionHistory.map((p: any) => p.cumulativeBonus),
                              borderColor: '#fa8c16',
                              backgroundColor: 'rgba(250, 140, 22, 0.1)',
                              fill: true,
                              tension: 0.4,
                              pointRadius: 2,
                              pointHoverRadius: 4,
                              yAxisID: 'y1',
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          interaction: {
                            mode: 'index',
                            intersect: false,
                          },
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                            tooltip: {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              padding: 12,
                            },
                          },
                          scales: {
                            y: {
                              type: 'linear',
                              display: true,
                              position: 'left',
                              title: {
                                display: true,
                                text: 'Nivel',
                              },
                              ticks: {
                                stepSize: 1,
                              },
                            },
                            y1: {
                              type: 'linear',
                              display: true,
                              position: 'right',
                              title: {
                                display: true,
                                text: 'Bonus ($)',
                              },
                              grid: {
                                drawOnChartArea: false,
                              },
                            },
                            x: {
                              grid: {
                                display: false,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            )}
          </>
        )}

        <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
          <Space>
            <GiftOutlined style={{ color: '#ac4949' }} />
            <Text strong style={{ fontSize: '16px' }}>Historial Detallado de Bonus</Text>
            <Badge count={record.bonusHistory?.length || 0} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        </Divider>

        {(() => {
          // Crear array de datos combinando firstLoginDate + bonusHistory
          const historyData: any[] = [];

          // Agregar fila especial de Primer Ingreso si existe
          if (record.firstLoginDate) {
            historyData.push({
              id: 'first-login-special',
              type: 'FIRST_LOGIN',
              createdAt: record.firstLoginDate,
              description: 'Primer ingreso exitoso a la plataforma',
              isSpecialRow: true,
            });
          }

          // Agregar el historial normal
          if (record.bonusHistory && record.bonusHistory.length > 0) {
            historyData.push(...record.bonusHistory);
          }

          return historyData.length > 0 ? (
            <>
              <style>{`
                .special-first-login-row {
                  background-color: ${theme === 'dark' ? 'rgba(19, 194, 194, 0.1)' : '#e6fffb'} !important;
                  border-left: 3px solid #13c2c2 !important;
                }
                .special-first-login-row:hover {
                  background-color: ${theme === 'dark' ? 'rgba(19, 194, 194, 0.2)' : '#b5f5ec'} !important;
                }
              `}</style>
              <Table
                columns={bonusColumns}
                dataSource={historyData}
                rowKey="id"
                pagination={{
                  defaultPageSize: 5,
                  pageSize: 5,
                  size: 'default',
                  showSizeChanger: true,
                  pageSizeOptions: ['5', '10', '25', '50'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
                  style: { marginTop: 16 }
                }}
                size="middle"
                loading={statsLoading}
                scroll={{ x: true }}
                rowClassName={(record) => record.isSpecialRow ? 'special-first-login-row' : ''}
              />
            </>
          ) : (
            <Empty
              description="Este jugador aún no ha recibido bonus"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: '40px 0' }}
            />
          );
        })()}
      </Card>
    );
};
